import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCheck, faTimes, faBuilding } from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import { useAuth } from '../../contexts/AuthContext';
import { useModalDismiss } from '../../hooks/useModalDismiss';

const CATEGORY_LABELS = {
  enqueteurs_cati: 'Enquêteurs CATI', enqueteurs_f2f: 'Prestataires F2F',
  superviseurs: 'Prestataires F2F', controle: 'Contrôle', deplacement: 'Déplacement',
  imprevus: 'Imprévus', autre: 'Autre',
};

const BudgetReview = () => {
  const { id, budgetId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [budget, setBudget] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState(null); // null, 'reject'
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [rejectionRemarks, setRejectionRemarks] = useState('');
  const [alert, setAlert] = useState(null);

  const approveDismiss = useModalDismiss(
    () => !saving && setShowApproveModal(false),
    { enabled: showApproveModal && !saving });

  const canApprove = ['administrateur_general', 'administrateur_general_superieur'].includes(user?.role);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const [bRes, pRes] = await Promise.all([
          api.get(`/projects/${id}/budgets/${budgetId}`),
          api.get(`/projects/${id}`),
        ]);
        setBudget(bRes.data.data);
        setProject(pRes.data.data);
      } catch { navigate(`/projects/${id}?tab=budget`); }
      finally { setLoading(false); }
    };
    fetch();
  }, [id, budgetId, navigate]);

  const handleApprove = async () => {
    setSaving(true);
    try {
      await api.post(`/projects/${id}/budgets/${budgetId}/approve`);
      setShowApproveModal(false);
      setAlert({ type: 'success', text: 'Budget approuvé' });
      const res = await api.get(`/projects/${id}/budgets/${budgetId}`);
      setBudget(res.data.data);
    } catch (e) { setAlert({ type: 'error', text: e.response?.data?.message || 'Erreur' }); }
    finally { setSaving(false); }
  };

  const handleReject = async () => {
    if (!rejectionRemarks.trim()) return;
    setSaving(true);
    try {
      await api.post(`/projects/${id}/budgets/${budgetId}/reject`, { rejection_remarks: rejectionRemarks.trim() });
      setAlert({ type: 'success', text: 'Budget rejeté' });
      setMode(null);
      setRejectionRemarks('');
      const res = await api.get(`/projects/${id}/budgets/${budgetId}`);
      setBudget(res.data.data);
    } catch (e) { setAlert({ type: 'error', text: e.response?.data?.message || 'Erreur' }); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600" /></div>;
  }

  if (!budget) {
    return <div style={{ textAlign: 'center', padding: 80, color: '#9ca3af' }}>Budget non trouvé</div>;
  }

  const isSubmitted = budget.status === 'submitted';

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '24px 16px' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
          Budget prévisionnel
        </div>
        <h4 style={{ fontWeight: 700, fontSize: '1.2rem', margin: 0, color: '#111827' }}>
          {budget.survey_slot?.name || 'N/A'}
        </h4>
        {project && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{project.name}</span>
            {project.client && (
              <span style={{ fontSize: '0.75rem', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 4 }}>
                <FontAwesomeIcon icon={faBuilding} size="xs" style={{ opacity: 0.5 }} />{project.client.name}
              </span>
            )}
          </div>
        )}
      </div>

      {alert && (
        <div style={{
          padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: '0.82rem',
          background: alert.type === 'success' ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${alert.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
          color: alert.type === 'success' ? '#166534' : '#991b1b',
        }}>{alert.text}</div>
      )}

      {/* Status badge */}
      <div style={{ marginBottom: 20 }}>
        <span style={{
          fontSize: '0.68rem', fontWeight: 600, padding: '3px 10px', borderRadius: 6,
          background: { draft: '#f3f4f6', submitted: '#fef9c3', approved: '#dcfce7', rejected: '#fee2e2' }[budget.status] || '#f3f4f6',
          color: { draft: '#6b7280', submitted: '#a16207', approved: '#166534', rejected: '#991b1b' }[budget.status] || '#6b7280',
        }}>
          {{ draft: 'Brouillon', submitted: 'Soumis', approved: 'Approuvé', rejected: 'Rejeté' }[budget.status]}
        </span>
      </div>

      {/* Cost summary */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 20px', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>Coût total</span>
          <span style={{ fontSize: '1.3rem', fontWeight: 700, color: '#111827' }}>{Number(budget.total_cost).toLocaleString('fr-FR')} MAD</span>
        </div>
      </div>

      {/* Lines table */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>
          <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Lignes budgétaires
          </span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              <th style={th}>Catégorie</th>
              <th style={th}>Libellé</th>
              <th style={{ ...th, textAlign: 'right' }}>Qté/Jrs</th>
              <th style={{ ...th, textAlign: 'right' }}>P.U.</th>
              <th style={{ ...th, textAlign: 'right' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {(budget.lines || []).map((line, i) => {
              const isLump = ['deplacement', 'imprevus', 'autre'].includes(line.category);
              return (
                <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={td}>
                    <span style={{ fontSize: '0.65rem', fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: '#f3f4f6', color: '#374151' }}>
                      {CATEGORY_LABELS[line.category] || line.category}
                    </span>
                  </td>
                  <td style={td}>{line.label || '—'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{isLump ? '—' : line.qty}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{isLump ? '—' : Number(line.unit_price).toLocaleString('fr-FR')}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{Number(line.total).toLocaleString('fr-FR')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Meta */}
      <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginBottom: 20 }}>
        Créé par: <strong style={{ color: '#374151' }}>{budget.created_by?.name || '—'}</strong>
        {budget.submitted_at && <> · Soumis le {new Date(budget.submitted_at).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })}</>}
        {budget.reviewed_by && <> · Examiné par <strong style={{ color: '#374151' }}>{budget.reviewed_by.name}</strong></>}
      </div>

      {/* Rejection input */}
      {mode === 'reject' && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20, marginBottom: 20 }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: 8 }}>
            Raison du rejet <span style={{ color: '#dc2626' }}>*</span>
          </div>
          <textarea
            rows={4} value={rejectionRemarks} onChange={e => setRejectionRemarks(e.target.value)}
            placeholder="Expliquez la raison du rejet..."
            style={{ width: '100%', fontSize: '0.82rem', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, outline: 'none', resize: 'vertical' }}
            onFocus={e => e.target.style.borderColor = '#dc2626'}
            onBlur={e => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
      )}

      {/* Actions */}
      {isSubmitted && canApprove && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {mode === null && (
            <>
              <button onClick={() => setMode('reject')} disabled={saving} style={btnDanger}>
                <FontAwesomeIcon icon={faTimes} size="xs" className="me-1" /> Rejeter
              </button>
              <button onClick={() => setShowApproveModal(true)} disabled={saving} style={btnSuccess}>
                <FontAwesomeIcon icon={faCheck} size="xs" className="me-1" /> Approuver
              </button>
            </>
          )}
          {mode === 'reject' && (
            <>
              <button onClick={() => { setMode(null); setRejectionRemarks(''); }} disabled={saving} style={btnSecondary}>Annuler</button>
              <button onClick={handleReject} disabled={saving || !rejectionRemarks.trim()}
                style={{ ...btnDanger, opacity: (!saving && rejectionRemarks.trim()) ? 1 : 0.5, cursor: (!saving && rejectionRemarks.trim()) ? 'pointer' : 'default' }}>
                {saving ? 'En cours...' : 'Confirmer le rejet'}
              </button>
            </>
          )}
        </div>
      )}

      {/* Approve confirmation modal */}
      {showApproveModal && budget && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.4)',
        }} {...approveDismiss}>
          <div style={{
            background: '#fff', borderRadius: 12, width: '100%', maxWidth: 440,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)', overflow: 'hidden',
            animation: 'approveModalIn 0.2s ease',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#111827' }}>Confirmer l'approbation</span>
              <button onClick={() => setShowApproveModal(false)} disabled={saving}
                style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: 4 }}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ fontSize: '0.82rem', color: '#374151', lineHeight: 1.6 }}>
                Voulez-vous vraiment approuver l'allocation de <strong>{Number(budget.total_cost).toLocaleString('fr-FR')} MAD</strong> pour l'enquête <strong>{budget.survey_slot?.name || 'N/A'}</strong> ?
              </div>
            </div>
            <div style={{ padding: '14px 20px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowApproveModal(false)} disabled={saving} style={btnSecondary}>Annuler</button>
              <button onClick={handleApprove} disabled={saving} style={btnSuccess}>
                {saving ? 'En cours...' : 'Approuver'}
              </button>
            </div>
          </div>
          <style>{`@keyframes approveModalIn { from { opacity:0; transform:translateY(10px) scale(0.98); } to { opacity:1; transform:translateY(0) scale(1); } }`}</style>
        </div>
      )}
    </div>
  );
};

const th = { padding: '8px 14px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: '0.72rem' };
const td = { padding: '8px 14px', color: '#111827', fontSize: '0.78rem' };
const btnSecondary = { padding: '8px 18px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer' };
const btnSuccess = { padding: '8px 18px', borderRadius: 6, border: 'none', background: '#10b981', color: '#fff', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' };
const btnDanger = { padding: '8px 18px', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' };

export default BudgetReview;
